import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdBadge, faAnchor, faTag, faMapMarkerAlt, faRoute,
  faTimes, faSpinner, faClock, faShieldHalved, faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import VesselIntelligencePanel from './VesselIntelligencePanel';
import { getFlagUrl } from '../../utils/flagUtils';
// The card reuses the .vw-card-flag / .vw-card-remove-btn rules that live in
// this stylesheet; import it directly so the card isn't styling-coupled to the
// watchlist panel happening to be mounted.
import './VesselWatchlistPanel.css';

// Keep just the human "Name" segment of a stored canonical port string
// ("Name, Country, UNLOCODE"); '' stays ''.
const portName = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  return s.includes(',') ? s.split(',')[0].trim() : s;
};

const fmtDateTime = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const durationSince = (val) => {
  if (!val) return '';
  const then = new Date(val);
  if (isNaN(then.getTime())) return '';
  const ms = Date.now() - then.getTime();
  if (ms < 0) return '';
  const totalHours = Math.floor(ms / 3600000);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return `${hours}h`;
  return '<1h';
};

/**
 * Watchlist vessel card for the Shipment Overview cards grid (shown when the
 * Deliveries / Watchlist toggle is set to Watchlist). Mirrors the delivery
 * card's visual language (.so-card family) but carries no delivery widgets:
 * just vessel identity, its user-entered load/discharge ports, live AIS, and a
 * compliance status. The top accent stripe encodes compliance (green = cleared,
 * red = sanctioned / not cleared) to match the map marker.
 */
const WatchlistVesselCard = ({
  entry,
  livePos = null,
  vesselKey = null,
  isHighlighted = false,
  isExpanded = false,
  onHover,
  onCardClick,
  onCollapse,
  onRemove,
  removing = false,
}) => {
  // Key used to link this card to its map marker/route. Falls back to the
  // watchlist key, but the parent passes the live position's key so a hull that
  // is also a delivery (`d:<id>`) still highlights + flies to correctly.
  const hoverKey = vesselKey || `w:${entry.id}`;
  const flagged = Boolean(livePos && livePos.complianceFlagged);
  const cleared = Boolean(livePos && livePos.sanctionsStatus === 'clean' && !flagged);
  const accentColor = flagged ? '#dc2626' : '#0d9488';

  const statusPill = flagged
    ? { label: 'Sanctioned', bg: '#fee2e2', color: '#b91c1c' }
    : cleared
      ? { label: 'Cleared', bg: '#d1fae5', color: '#065f46' }
      : { label: 'Watching', bg: '#ccfbf1', color: '#115e59' };

  const loadPort = portName(entry.loading_port_location);
  const dischPort = portName(entry.discharging_port_location);
  const dwell = livePos ? durationSince(livePos.nav_status_since) : '';
  const statusLabel = livePos && livePos.nav_status
    ? (dwell ? `${livePos.nav_status} · ${dwell}` : livePos.nav_status)
    : '';
  const aisEta = livePos ? fmtDateTime(livePos.eta) : '';

  return (
    <div
      className={`so-card ${isHighlighted ? 'so-card-highlighted' : ''} ${isExpanded ? 'so-card-expanded' : ''}`}
      style={{ '--card-accent': accentColor }}
      onMouseEnter={() => onHover && onHover(hoverKey)}
      onMouseLeave={() => onHover && onHover(null)}
    >
      <div className="so-card-accent" style={{ background: accentColor }} />

      <div
        className="so-card-face"
        role="button"
        tabIndex={0}
        onClick={() => onCardClick && onCardClick(entry)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick && onCardClick(entry);
          }
        }}
      >
        <div className="so-card-title-row">
          <span
            className="so-card-status"
            style={{ background: statusPill.bg, color: statusPill.color }}
          >
            {statusPill.label}
          </span>
          <span className="so-card-name">{entry.vessel_name || 'Unknown vessel'}</span>
          {entry.flag && getFlagUrl(entry.flag) ? (
            <img
              src={getFlagUrl(entry.flag)}
              alt={entry.flag}
              className="vw-card-flag"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <span className="so-card-id">{entry.flag || '--'}</span>
          )}
        </div>

        <div className="so-card-metrics">
          <div className="so-card-metric">
            <FontAwesomeIcon icon={faIdBadge} className="so-card-metric-icon" style={{ color: accentColor }} />
            <div className="so-card-metric-content">
              <span className="so-card-metric-label">IMO</span>
              <span className="so-card-metric-value">{entry.imo || '--'}</span>
            </div>
          </div>
          <div className="so-card-metric">
            <FontAwesomeIcon icon={faAnchor} className="so-card-metric-icon" style={{ color: accentColor }} />
            <div className="so-card-metric-content">
              <span className="so-card-metric-label">MMSI</span>
              <span className="so-card-metric-value">{entry.mmsi || '--'}</span>
            </div>
          </div>
          <div className="so-card-metric">
            <FontAwesomeIcon icon={faTag} className="so-card-metric-icon" style={{ color: accentColor }} />
            <div className="so-card-metric-content">
              <span className="so-card-metric-label">Type</span>
              <span className="so-card-metric-value">{entry.ship_type || 'Unknown'}</span>
            </div>
          </div>
          <div className="so-card-metric">
            <FontAwesomeIcon icon={faAnchor} className="so-card-metric-icon" style={{ color: '#28a745' }} />
            <div className="so-card-metric-content">
              <span className="so-card-metric-label">Load</span>
              <span className="so-card-metric-value">{loadPort || '—'}</span>
            </div>
          </div>
          <div className="so-card-metric">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="so-card-metric-icon" style={{ color: '#dc2626' }} />
            <div className="so-card-metric-content">
              <span className="so-card-metric-label">Disch</span>
              <span className="so-card-metric-value">{dischPort || '—'}</span>
            </div>
          </div>
          {statusLabel && (
            <div className="so-card-metric">
              <FontAwesomeIcon icon={faRoute} className="so-card-metric-icon" style={{ color: accentColor }} />
              <div className="so-card-metric-content">
                <span className="so-card-metric-label">Status</span>
                <span className="so-card-metric-value">{statusLabel}</span>
              </div>
            </div>
          )}
          {aisEta && (
            <div className="so-card-metric">
              <FontAwesomeIcon icon={faClock} className="so-card-metric-icon" style={{ color: accentColor }} />
              <div className="so-card-metric-content">
                <span className="so-card-metric-label">AIS ETA</span>
                <span className="so-card-metric-value">{aisEta}</span>
              </div>
            </div>
          )}
        </div>

        <div className="so-card-footer">
          <span className="so-card-footer-hint">
            <FontAwesomeIcon icon={faShieldHalved} style={{ marginRight: '0.3rem', color: flagged ? '#dc2626' : cleared ? '#1e7e34' : '#9ca3af' }} />
            {flagged ? 'Sanctioned / not cleared' : cleared ? 'Cleared' : 'Not screened'}
          </span>
          <button
            type="button"
            className="vw-card-remove-btn"
            onClick={(e) => { e.stopPropagation(); onRemove && onRemove(entry); }}
            disabled={removing}
            title="Remove from watchlist"
          >
            {removing
              ? <FontAwesomeIcon icon={faSpinner} spin />
              : <><FontAwesomeIcon icon={faTimes} /> Remove</>}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="so-card-expanded-body">
          <div className="so-card-toolbar">
            <div className="so-card-toolbar-left">
              <button
                type="button"
                className="so-card-back-btn"
                onClick={(e) => { e.stopPropagation(); onCollapse && onCollapse(); }}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> All watchlist
              </button>
            </div>
          </div>
          <div className="so-card-content-area">
            {entry.imo ? (
              <VesselIntelligencePanel imo={entry.imo} />
            ) : (
              <div style={{ padding: '1rem 1.25rem', color: '#6b7280', fontSize: '0.85rem' }}>
                This vessel has no IMO number, so sanctions &amp; compliance screening isn’t available.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchlistVesselCard;
